import * as THREE from 'three';
import type {
  GlbAnalysisSummary,
  GlbMaterialFinding,
  ParsedGlbBinary,
  ParsedRuntimeGlb,
} from './types';

export function analyzeSceneSummary(
  file: File,
  binary: ParsedGlbBinary,
  runtime: ParsedRuntimeGlb,
  animatedCameraClipCount: number,
  animatedCameraNodeCount: number,
  materialFindings: GlbMaterialFinding[] = [],
): GlbAnalysisSummary {
  const materials = new Set<THREE.Material>();
  const textures = new Set<THREE.Texture>();
  let meshCount = 0;
  let totalTriangles = 0;
  let totalVertices = 0;

  runtime.scene?.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;

    meshCount += 1;

    // Geometry statistics
    const geometry = (object as THREE.Mesh).geometry;
    if (geometry) {
      const positionCount = geometry.attributes.position?.count ?? 0;
      totalVertices += positionCount;
      totalTriangles += geometry.index
        ? geometry.index.count / 3
        : positionCount / 3;
    }

    const meshMaterials = Array.isArray(object.material) ? object.material : [object.material];
    meshMaterials.forEach((material) => {
      if (!material) return;
      materials.add(material);

      Object.values(material as Record<string, unknown>).forEach((value) => {
        if (value instanceof THREE.Texture) {
          textures.add(value);
        }
      });
    });
  });

  return {
    fileName: file.name,
    fileSizeBytes: file.size,
    glbVersion: binary.version,
    sceneCount: binary.sceneCount,
    meshCount,
    triangleCount: Math.floor(totalTriangles),
    vertexCount: totalVertices,
    materialCount: materials.size,
    textureCount: textures.size,
    animationClipCount: runtime.animations.length,
    animatedCameraClipCount,
    animatedCameraNodeCount,
    standardMaterialCount: materialFindings.filter(
      (material) => material.runtimeClass === 'MeshStandardMaterial',
    ).length,
    physicalMaterialCount: materialFindings.filter(
      (material) => material.runtimeClass === 'MeshPhysicalMaterial',
    ).length,
    unlitMaterialCount: materialFindings.filter(
      (material) => material.extensionKeys.includes('KHR_materials_unlit'),
    ).length,
    alphaBlendMaterialCount: materialFindings.filter(
      (material) => material.alphaMode === 'BLEND',
    ).length,
    doubleSidedMaterialCount: materialFindings.filter((material) => material.doubleSided).length,
  };
}
